import type { GrammarSummary } from "@/lib/search";

/**
 * What 文法 draws, as a pure function of the contents and the filters (日本-519).
 *
 * `libraryRows.ts` and `resultKind.ts` are the precedent and the reason: `web/` has no rendering
 * tests, so a rule left inside a component is checked by reading and by nothing else — and the
 * interesting questions here (which volume a row belongs under, what a filter does to the headers,
 * what an empty result means) have no DOM in them.
 */

/** The three volumes, in READING order. `ORDER BY volume` would lead with Advanced. */
export const VOLUMES = ["Basic", "Intermediate", "Advanced"] as const;
export type Volume = (typeof VOLUMES)[number];

// One definition of each, in the module Read's dock already imports them from — see there.
export { VOLUME_LABEL, citation } from "@/components/dictionary/resultKind";

export interface GrammarFilters {
  /** A source volume name, or null for 全巻. */
  volume: string | null;
  /** A source part-of-speech HEAD, or null for すべて. */
  partOfSpeech: string | null;
}

/** A volume heading, or an entry. The list is one flat array so the DOM order IS the reading order. */
export type GrammarRow =
  | { kind: "heading"; volume: string; count: number }
  | { kind: "entry"; entry: GrammarSummary };

/**
 * Apply the two filters. **Search is not one of them** — that is the server's, because the key rule
 * lives in `engine/grammar_keys.py` and nothing else may restate it.
 */
export function filterEntries(
  entries: readonly GrammarSummary[],
  filters: GrammarFilters,
): GrammarSummary[] {
  return entries.filter((entry) => {
    if (filters.volume && entry.volume !== filters.volume) return false;
    if (filters.partOfSpeech) {
      const head = (entry.partOfSpeech ?? "").replace(/\s*[（(].*$/, "").trim();
      if (head !== filters.partOfSpeech) return false;
    }
    return true;
  });
}

/**
 * The rows to draw, with a volume heading before each run.
 *
 * **Headings are derived from the entries rather than emitted per volume**, which is what makes a
 * filtered list honest: filter to 助詞 and the Advanced heading disappears if no Advanced entry is
 * a particle, rather than sitting over nothing. Its count is what SURVIVED the filter, not the
 * volume's size — a heading saying 228項 above four rows is a heading that lies.
 *
 * The entries are assumed to arrive in the server's order (volume, then page), which is the book's
 * own; this groups a sequence rather than sorting one, so a server that stopped ordering would show
 * as repeated headings rather than as silently scrambled contents.
 */
export function toRows(entries: readonly GrammarSummary[]): GrammarRow[] {
  const rows: GrammarRow[] = [];
  const counts = new Map<string, number>();
  for (const entry of entries) counts.set(entry.volume, (counts.get(entry.volume) ?? 0) + 1);

  let current: string | null = null;
  for (const entry of entries) {
    if (entry.volume !== current) {
      current = entry.volume;
      rows.push({ kind: "heading", volume: entry.volume, count: counts.get(entry.volume) ?? 0 });
    }
    rows.push({ kind: "entry", entry });
  }
  return rows;
}

/**
 * The count line: `629項` for the whole contents, `てしまう · 2項` for a search.
 *
 * The query leads when there is one, because that is what the number is *about* — a bare count over
 * a filtered list reads as the size of the dictionary.
 */
export function countLabel(query: string, shown: number, filtered: boolean): string {
  const count = `${shown}項`;
  if (query.trim()) return `${query.trim()} · ${count}`;
  return filtered ? `${count}（絞り込み中）` : count;
}

/**
 * Where the keyboard should land after a filter or a query changes.
 *
 * **Always the top**, and it is worth stating: `focusedRow` indexes the list on screen, so keeping
 * an index across a change of list points it at a different entry — the same class of bug
 * `ResultList` documents about indexing `results` instead of `rows`.
 */
export function resetFocus(): number {
  return 0;
}

/**
 * Move the focus by one ENTRY, stepping over headings.
 *
 * Headings are rows so the list is one array, but they are not places you can be: landing on one
 * would make ↵ do nothing and read as the key having failed. Clamped at both ends rather than
 * wrapping, on the results list's rule.
 */
export function moveFocus(rows: readonly GrammarRow[], from: number, delta: 1 | -1): number {
  // **`from`, not wherever the walk stopped.** Stepping up from the first entry walks onto the
  // heading above it and then off the start — and returning the walk's own position would leave
  // the focus ON that heading, where ↵ does nothing and the key reads as broken. Found by the test
  // rather than by reading: the two are the same value everywhere except at the ends.
  let index = from;
  for (;;) {
    const next = index + delta;
    if (next < 0 || next >= rows.length) return from;
    index = next;
    if (rows[index]?.kind === "entry") return index;
  }
}

/** The first entry row, for a fresh list. `-1` when the filters left nothing. */
export function firstEntry(rows: readonly GrammarRow[]): number {
  return rows.findIndex((row) => row.kind === "entry");
}

/**
 * 接続's own rules moved to `formationCells.ts` in 日本-524.
 *
 * `formationLines` lived here while 接続 was a flat string to be broken up by heuristic. The deck's
 * table survives the extraction now, so reading it is a parse rather than a repair and it has a
 * module of its own — with the old function kept there as the fallback for the one entry that
 * still arrives flat.
 */
export { formationLines, readFormation } from "./formationCells";
